import { Hls, HlsVideoElement } from 'hls-video-element';
import { CastableMediaMixin } from 'castable-video/castable-mixin.js';
import { VASTAdsMixin } from '../vast-hls-video.js';
import {
  getBestCodecType,
  getCodecDisplayName,
  getLevelsToRemove,
} from '../utils/codec-filter.js';
import { StreamDrmEngine } from '../utils/stream-drm-engine.js';
import { isFairPlayBrowser } from '../utils/browser-detection.js';

class BunnyStreamVideoElement
  extends VASTAdsMixin(CastableMediaMixin(HlsVideoElement)) {
  static get observedAttributes() {
    return (super.observedAttributes || []).concat([
      'drm-robustness',
      'drm-version',
      'video-id',
      'library-id',
      'drm-engine',
      'drm-provider',
      'is-jit',
    ]);
  }

  _waitingKeys = false;
  _edrmEnabled = false;
  _videoId = this.getAttribute('video-id');
  _libraryId = this.getAttribute('library-id');
  _drmEngine = this.getAttribute('drm-engine');
  _drmProvider = this.getAttribute('drm-provider');
  _drmVersion = this.#parseDrmVersion(this.getAttribute('drm-version'));

  /**
   * Parse DRM version attribute value to integer
   * @param {string|null} value - Attribute value to parse
   * @returns {number|null} Parsed integer or null if value is invalid/absent
   */
  #parseDrmVersion(value) {
    return value ? parseInt(value, 10) : null;
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (name === 'drm-version') {
      this._drmVersion = this.#parseDrmVersion(newVal);
      this._edrmEnabled = this._drmVersion === 1;
    }
    if (name === 'video-id') this._videoId = newVal;
    if (name === 'library-id') this._libraryId = newVal;
    if (name === 'drm-engine') this._drmEngine = newVal;
    if (name === 'drm-provider') this._drmProvider = newVal;
    if (name === 'is-jit') {
      this.isJit = newVal;
    }
    super.attributeChangedCallback?.(name, oldVal, newVal);
  }

  constructor() {
    super();
    this.mediaSourceSupported = this.#checkMediaSourceSupport();
    this.isJit = false;

    // Initialize _edrmEnabled from _drmVersion property
    this._edrmEnabled = this._drmVersion === 1;

    // Make element focusable for keyboard events
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0');
    }

    // Add keyboard event listeners for volume control
    this.addEventListener('keydown', this.#handleKeyDown.bind(this));

    // Configure HLS.js with EME disabled for internal DRM (only if DRM is enabled)
    const basicHlsConfig = {
      debug: true,
      emeEnabled: this._edrmEnabled && this._drmEngine != 'internal',
      useDevicePixelRatio: true,
      capLevelToPlayerSize: true,
      enableWorker: true,
      autoStartLoad: true,
      maxMaxBufferLength: 120,
      maxBufferLength: 120,
    };

    Object.assign(Hls.DefaultConfig, basicHlsConfig);
  }

  init() {
    super.init();
    // Ensure shadow root exists (HlsVideoElement likely creates it, but confirm)
    if (!this.shadowRoot) {
      this.attachShadow({ mode: 'open' });
    }
  }

  /**
   * Detect the maximum available Widevine robustness level supported by the browser
   * @returns {Promise<string>} The highest supported robustness level
   */
  async #detectMaxRobustness() {
    // Robustness levels to test in priority order (L1 hardware -> L3 software)
    const robustnessLevels = [
      'HW_SECURE_CRYPTO', // L1: Hardware-backed crypto
      'HW_SECURE_DECODE', // L1: Hardware-backed decode
      'SW_SECURE_CRYPTO', // L3: Software-based
    ];

    // Test each robustness level using EME API
    for (const level of robustnessLevels) {
      try {
        const config = [{
          initDataTypes: ['cenc'],
          videoCapabilities: [{
            contentType: 'video/mp4; codecs="avc1.42E01E"',
            robustness: level,
          }],
        }];

        await navigator.requestMediaKeySystemAccess(
          'com.widevine.alpha',
          config,
        );
        console.debug(`Detected max Widevine robustness: ${level}`);
        return level;
      } catch (_error) {
        // This level not supported, try next
        continue;
      }
    }

    // Fallback to software-based if detection fails
    console.debug(
      'Widevine robustness detection failed, using SW_SECURE_CRYPTO',
    );
    return 'SW_SECURE_CRYPTO';
  }

  async #getRobustness() {
    const drmRobustness = this.getAttribute('drm-robustness');

    // If backend provides a minimum robustness level, respect it
    if (drmRobustness) {
      switch (+drmRobustness) {
        case 1:
          return {
            videoRobustness: 'HW_SECURE_DECODE',
          };
        case 2:
          return {
            videoRobustness: 'HW_SECURE_CRYPTO',
          };
        case 3:
          return {
            videoRobustness: 'SW_SECURE_CRYPTO',
          };
      }
    }

    // No backend setting: detect and use maximum available robustness
    const maxRobustness = await this.#detectMaxRobustness();
    return {
      videoRobustness: maxRobustness,
    };
  }

  #checkMediaSourceSupport() {
    try {
      return typeof MediaSource !== 'undefined';
    } catch (_e) {
      return false;
    }
  }

  /**
   * Handle keyboard events for volume control
   */
  #handleKeyDown(event) {
    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        this.volume = Math.min(1, this.volume + 0.1);
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.volume = Math.max(0, this.volume - 0.1);
        break;
    }
  }

  /**
   * Apply codec filtering to HLS levels
   */
  #applyCodecFiltering(levels) {
    if (!Array.isArray(levels) || !this.mediaSourceSupported) return;

    // Determine the best codec type to keep
    const bestCodecType = getBestCodecType(levels);

    if (!bestCodecType) {
      console.debug('playing x264');
      return; // Keep all levels
    }

    console.debug(`playing ${getCodecDisplayName(bestCodecType)}`);

    // Get indices of levels to remove (in reverse order to avoid index shifting)
    const levelsToRemove = getLevelsToRemove(levels, bestCodecType);

    // Remove levels using HLS.js API
    levelsToRemove.forEach((index) => {
      this.api.removeLevel(index);
    });
  }

  /**
   * Parse URL path into key-value pairs
   * @param {string} url - URL to parse
   * @returns {Map<string, string>} Map of path segments
   */
  #parseUrlPath(url) {
    const routes = new Map();
    const p = new URL(url).pathname.split('/');
    for (let i = 1; i < p.length - 1; i += 2) {
      routes.set(p[i], p[i + 1]);
    }
    return routes;
  }

  generateDrmRequest(initDataType, initData, _keyContext) {
    return { initDataType, initData };
  }

  getRoutes(request) {
    return this.#parseUrlPath(request);
  }

  async load() {
    // Only configure DRM if drmVersion is 1
    if (this._edrmEnabled) {
      if (this._drmEngine === 'internal') {
        const url = new URL(globalThis.location.href);
        const token = url.searchParams.get('token') || '';
        const expires = parseInt(url.searchParams.get('expires') || '0', 10);

        let queryParams = '';
        if (token && expires) {
          queryParams = `token=${token}&expires=${expires}`;
        }

        // Create StreamDrmEngine but don't initialize yet
        globalThis.streamDrmEngine = new StreamDrmEngine(
          `https://video.bunnycdn.com/FairPlay/${this._libraryId}/license?videoId=${this._videoId}${queryParams}`,
          `https://video.bunnycdn.com/FairPlay/${this._libraryId}/certificate`,
          0,
        );
      } else {
        // Configure HLS.js DRM for non-internal engines
        const url = new URL(globalThis.location.href);
        const token = url.searchParams.get('token') || '';
        const expires = parseInt(url.searchParams.get('expires') || '0', 10);

        let queryParams = '';
        if (token && expires) {
          queryParams = `?token=${token}&expires=${expires}`;
        }

        const isFairPlay = isFairPlayBrowser();
        const hlsDrmConfig = {
          emeEnabled: true,
          drmSystemOptions: isFairPlay
            ? {}
            : await this.#getRobustness(),
          drmSystems: {
            'com.widevine.alpha': {
              licenseUrl:
                `https://video.bunnycdn.com/WidevineLicense/${this._libraryId}/${this._videoId}${queryParams}`,
              generateRequest: this.generateDrmRequest,
            },
            'com.apple.fps': {
              licenseUrl:
                `https://video.bunnycdn.com/FairPlay/${this._libraryId}/license?videoId=${this._videoId}${queryParams}`,
              serverCertificateUrl:
                `https://video.bunnycdn.com/FairPlay/${this._libraryId}/certificate`,
            },
          },
        };

        Object.assign(Hls.DefaultConfig, hlsDrmConfig);
      }
    }

    // Call parent load
    await super.load();

    // Only proceed with DRM initialization if DRM is enabled
    if (this._edrmEnabled && this._drmEngine === 'internal') {
      try {
        // Now find and use the real HTMLVideoElement

        let realVideoElement = null;

        // Check if HLS.js api has media reference (most likely)
        if (this.api && this.api.media) {
          realVideoElement = this.api.media;
        }

        // Fallback checks
        if (!realVideoElement) {
          if (this.shadowRoot) {
            const videoInShadow = this.shadowRoot.querySelector('video');
            if (videoInShadow) realVideoElement = videoInShadow;
          }
        }

        if (!realVideoElement) {
          const videoChild = this.querySelector('video');
          if (videoChild) realVideoElement = videoChild;
        }

        if (!realVideoElement) {
          throw new Error(
            'Could not find real HTMLVideoElement - encrypted events will fail',
          );
        }

        // Initialize StreamDrmEngine with the REAL video element
        await globalThis.streamDrmEngine.initFairPlay(realVideoElement);
      } catch (error) {
        console.error('❌ StreamDrmEngine initialization failed:', error);
        throw error;
      }

      return;
    }

    // Only proceed if we have HLS.js support and an api instance
    if (!this.api || !this.src) {
      return;
    }

    // Configure xhrSetup if handler is available
    if (this.isJit === 'true' || this.isJit === true || this.isJit === '1') {
      this.api.config.xhrSetup = (xhr, url) => {
        // Extract the session from the querystring
        const requestUrl = new URL(url);

        const routes = this.getRoutes(url);

        const guid = routes.get('guid');
        const session = routes.get('session');

        xhr.open(
          'GET',
          `/${guid}/${session}/${requestUrl.pathname.split('/').pop()}`,
        );
      };
    }

    // Bind HLS.js events
    this.api.on(Hls.Events.MANIFEST_PARSED, (_event, data) => {
      this.#applyCodecFiltering(data.levels);
    });

    this.api.on(Hls.Events.ERROR, (_event, data) => {
      if (!data.fatal) return;

      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR:
          console.debug('fatal network error encountered, trying to recover');
          this.api.startLoad();
          break;
        case Hls.ErrorTypes.MEDIA_ERROR:
          console.debug('fatal media error encountered, trying to recover');
          this.api.recoverMediaError();
          break;
        default:
          console.debug('unrecoverable error');
          this.api.destroy();
          break;
      }
    });
  }
}

customElements.define('bunny-stream-video', BunnyStreamVideoElement);

export default BunnyStreamVideoElement;
